import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Home as HomeIcon, Droplet, Waves, Utensils, Armchair, PawPrint, FileText, Users, CreditCard, MapPin, Building2, ShoppingBag, Car, MapPinned } from 'lucide-react';
import ImageWithSkeleton from '../components/ImageWithSkeleton';

const Home = () => {
  const navigate = useNavigate();

  const amenities = [
    { icon: <Waves className="w-5 h-5" />, text: 'In-Unit Washer / Dryer' },
    { icon: <Utensils className="w-5 h-5" />, text: 'Gourmet Kitchens w/ Stainless Steel Appliances' },
    { icon: <HomeIcon className="w-5 h-5" />, text: 'Oversized Windows' },
    { icon: <Utensils className="w-5 h-5" />, text: 'Dishwashers & Microwaves' },
    { icon: <Droplet className="w-5 h-5" />, text: 'Tiled Shower Tubs' },
    { icon: <Waves className="w-5 h-5" />, text: 'Air Conditioning & Heating' },
    { icon: <HomeIcon className="w-5 h-5" />, text: 'Hardwood Floors' },
    { icon: <Car className="w-5 h-5" />, text: 'On-Site Parking' },
    { icon: <Building2 className="w-5 h-5" />, text: 'Walk to Campus' },
    { icon: <MapPin className="w-5 h-5" />, text: 'Downtown Boone at Your Doorstep' }
  ];

  const floorPlans = [
    {
      number: '1',
      title: '1 BEDROOM',
      description: 'Roomy layout to live and work',
      image: 'https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/12gxn0cb_Rowefeb25-04791-13f207f5b370b7a057ca0bc1225f390e.jpg'
    },
    {
      number: '2',
      title: '2 BEDROOM',
      description: 'Comfortable living for two',
      image: 'https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/1d30rfhm_Homepage%20Floorplans_%203%20Bedroom%20Thumbnail.jpg'
    },
    {
      number: '3',
      title: '3 BEDROOM',
      description: 'Room to spread out with friends',
      image: 'https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/1d30rfhm_Homepage%20Floorplans_%203%20Bedroom%20Thumbnail.jpg'
    }
  ];

  const locationFeatures = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'NEXT TO CAMPUS',
      description: 'Cross the street to Appalachian State University'
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: 'CENTER OF DOWNTOWN',
      description: '400 W King Street & 271 Wallace Circle, Boone, NC 28607'
    },
    {
      icon: <Car className="w-6 h-6" />,
      title: 'ON SITE PARKING',
      description: 'Available for residents'
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: 'SHOPPING & DINING',
      description: 'Restaurants & coffee next door + free shops'
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section 
        className="relative h-[600px] flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/w54ffhag_Homepage%20hero%20image.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 text-white animate-fade-in-up">
            THE SUMMIT ON KING
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl mb-4 text-white animate-fade-in-up animation-delay-100">
            DOWNTOWN BOONE. STEPS TO CAMPUS
          </p>
          <p className="text-lg md:text-xl mb-10 max-w-4xl mx-auto leading-relaxed text-white/90 animate-fade-in-up animation-delay-200">
            Convenient, High-Quality Living in Boone, NC
          </p>
          <div className="animate-fade-in-up animation-delay-300">
            <Button 
              size="lg" 
              className="bg-[#FF5722] hover:bg-white text-white hover:text-[#FF5722] border-2 border-[#FF5722] px-10 py-7 text-lg font-bold uppercase transition-all duration-300 hover-lift"
              onClick={() => navigate('/floor-plans')}
            >
              VIEW FLOOR PLANS
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24">
        <div className="w-full max-w-none px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-12 text-center">
              <p className="text-[#FF5722] font-bold text-sm md:text-base tracking-widest uppercase mb-6 animate-fade-in">
                THE SUMMIT ON KING
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-10 leading-tight text-black animate-fade-in-up animation-delay-100">
                THE BEST OF BOONE -<br />RIGHT OUTSIDE YOUR DOOR
              </h2>
              <p className="text-lg md:text-xl text-black mb-12 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
                Located on King Street directly across from Appalachian State University, The Summit on King places you in the heart of Downtown Boone. Walk to campus, coffee and dining. Enjoy brand-new 1-, 2-, and 3-bedroom apartments with modern finishes. High-quality living at Boone's most walkable address.
              </p>
              <div className="flex justify-center animate-fade-in-up animation-delay-300">
                <Button 
                  size="lg" 
                  className="bg-[#FF5722] hover:bg-white text-white hover:text-[#FF5722] border-2 border-[#FF5722] px-10 py-7 text-base font-bold uppercase transition-all duration-300 hover-lift"
                  onClick={() => navigate('/floor-plans')}
                >
                  EXPLORE FLOORPLANS
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="w-full max-w-none px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch max-w-7xl mx-auto">
            <div>
              <ImageWithSkeleton
                src="https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/6qsnm5ma_Hompage%20Luxury%20amenities.png"
                alt="Modern kitchen"
                className="rounded-sm shadow-lg"
                aspectRatio="h-full"
                objectFit="object-cover"
              />
            </div>
            <div className="flex flex-col justify-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black">
                LUXURY<br />
                <span className="text-[#FF5722]">AMENITIES</span>
              </h2>
              <p className="text-lg md:text-xl text-black mb-10 leading-relaxed">
                Enjoy newly-renovated apartments designed for comfort and convenience — featuring modern interiors, premium finishes, and a location that puts you in the center of it all.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <span className="text-[#FF5722] flex-shrink-0">{amenity.icon}</span>
                    <span className="text-black font-medium">{amenity.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-center lg:justify-start">
              <Button 
                size="lg" 
                className="bg-[#FF5722] hover:bg-white text-white hover:text-[#FF5722] border-2 border-[#FF5722] px-8 py-5 text-base font-semibold uppercase transition-all duration-200"
                onClick={() => navigate('/amenities')}
              >
                EXPLORE AMENITIES
              </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floor Plans Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-wider">
              SPACIOUS<br />
              <span className="text-[#FF5722]">FLOORPLANS</span>
            </h2>
            <p className="text-lg md:text-xl text-black max-w-3xl mx-auto">
              Choose from spacious studio, 1-, 2- & 3-bedroom apartments —located on King Street and walkable to Appalachian State.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {floorPlans.map((plan, index) => (
              <div key={index} className="group relative overflow-hidden rounded-lg">
                <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
                  <img 
                    src={plan.image} 
                    alt={plan.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 w-16 h-16 bg-[#FF5722] rounded-full flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">{plan.number}</span>
                  </div>
                </div>
                <div className="py-8 text-center">
                <h3 className="font-black text-2xl mb-2 tracking-wider">{plan.title}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                <button 
                  className="text-[#FF5722] font-bold text-lg uppercase tracking-wide hover:underline transition-all inline-flex items-center gap-2"
                  onClick={() => navigate('/floor-plans')}
                >
                  VIEW UNITS →
                </button>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button 
              size="lg" 
              className="bg-[#FF5722] hover:bg-white text-white hover:text-[#FF5722] border-2 border-[#FF5722] px-8 py-6 text-base font-semibold uppercase transition-all duration-200"
              onClick={() => navigate('/floor-plans')}
            >
              VIEW ALL FLOOR PLANS
            </Button>
          </div>
        </div>
      </section>

      {/* Community Features Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Image */}
            <div className="order-2 lg:order-1 animate-slide-left">
              <img 
                src="https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/r5fgh5uw_Downtown%20full%20campus%20view%20copy.jpg"
                alt="Aerial view showing The Summit on King and Appalachian State University"
                className="w-full h-auto object-contain rounded-sm shadow-lg"
              />
            </div>
            
            {/* Right side - Content */}
            <div className="order-1 lg:order-2 animate-slide-right">
              <h2 className="text-4xl md:text-5xl font-bold mb-2 text-black">
                <span className="text-[#FF5722]">COMMUNITY FEATURES</span>
                <br />
                THE MOST WALKABLE ADDRESS IN BOONE
              </h2>
              <p className="text-lg md:text-xl text-black mb-8 leading-relaxed">
                Step outside your door and you're in the heart of Downtown Boone. Walk to App State, grab coffee around the corner, enjoy dinner nearby – and be home in minutes.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FF5722] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-black text-lg">Walk to Appalachian State University</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FF5722] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-black text-lg">Restaurants, coffee, and local favorites are steps away</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FF5722] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-black text-lg">On King Street or just above it – find your perfect home at The Summit on King</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FF5722] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-black text-lg">On-site parking available</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FF5722] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-black text-lg">Adjacent to two AppalCART bus stops</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FF5722] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-black text-lg">Scenic river views</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FF5722] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-black text-lg">Quick access to downtown Boone</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FF5722] rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-black text-lg">Safe, residential area</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-wider">
                PERFECT<br />
                <span className="text-[#FF5722]">LOCATION</span>
              </h2>
              <p className="text-lg md:text-xl text-black mb-8">
                Located in the heart of Boone, The Summit on King offers unparalleled access to everything you need.
              </p>
              <div className="space-y-6">
                {locationFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="p-2 bg-orange-100 rounded-sm flex-shrink-0">
                      <span className="text-[#FF5722]">{feature.icon}</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">{feature.title}</h3>
                      <p className="text-black">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full">
              <div className="rounded-sm shadow-lg overflow-hidden h-[500px]">
                <iframe
                  src="https://maps.google.com/maps?q=400+W+King+St,+Boone,+NC+28607&z=16&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="The Summit on King Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;